import { createSelector } from 'reselect';
import { AppState } from '../../';
import { FeatureFlags } from '@fullstory/client-api';
import {
  SpecObject,
  SpecObjectConfig,
  SpecObjectMappingRecord,
  SpecObjectProperty,
  SpecPropertyMapping,
} from '@fullstory/proto/fs/services/search-settings';
import { getIsReady } from '@fullstory/redux-util';
import { topSpecObjectSlugs } from './constants';
import { WithRequiredProperty, isNonNull } from '@fullstory/typeutils';
import { getFeatureFlags } from '../fullstory/selectors';
import { keyBy, pickBy } from 'lodash';
import {
  BySlug,
  SpecInfo,
  SpecObjectInfo,
  SpecObjectMappingInfo,
  TargetType,
  toSpecInfo,
  toSpecObjectInfo,
  toSpecObjectMappingInfo,
} from './entities';

const getSpecState = (state: AppState) => state.specs;
const getOrgIndustry = (state: AppState) => state.fullstory.orgIndustry.toLowerCase();
const getFlags = (state: AppState) => state.fullstory.featureFlags;

export const getFsSpecEnabled = createSelector(getFlags, flags => flags.AppFsSpec);

const getSpecsDefinitionsLoading = createSelector(
  getSpecState,
  specState => specState.specsRequest.loading,
);
const getSpecsDefinitionsReady = createSelector(getSpecState, specState =>
  getIsReady(specState.specsRequest),
);
const getSpecConfigsLoading = createSelector(
  getSpecState,
  specState => specState.specConfigsRequest.loading,
);
const getSpecConfigsReady = createSelector(getSpecState, specState =>
  getIsReady(specState.specConfigsRequest),
);
const getSpecObjectConfigsLoading = createSelector(
  getSpecState,
  specState => specState.specObjectConfigsRequest.loading,
);
const getSpecObjectConfigsReady = createSelector(getSpecState, specState =>
  getIsReady(specState.specObjectConfigsRequest),
);
const getSpecMappingsLoading = createSelector(
  getSpecState,
  specState => specState.specMappingsRequest.loading,
);
const getSpecMappingsReady = createSelector(getSpecState, specState =>
  getIsReady(specState.specMappingsRequest),
);

export const getSpecsLoading = createSelector(
  getSpecsDefinitionsLoading,
  getSpecConfigsLoading,
  getSpecObjectConfigsLoading,
  getSpecMappingsLoading,
  (defsLoading, specConfigsLoading, objectConfigsLoading, mappingsLoading) =>
    defsLoading || specConfigsLoading || objectConfigsLoading || mappingsLoading,
);

export const getSpecsReady = createSelector(
  getSpecsDefinitionsReady,
  getSpecConfigsReady,
  getSpecObjectConfigsReady,
  getSpecMappingsReady,
  (defsReady, specConfigsReady, objectConfigsReady, mappingsReady) =>
    defsReady && specConfigsReady && objectConfigsReady && mappingsReady,
);

// "Raw" selectors which describe the spec configuration in something
// approximating the wire format. These selectors are implementation details of
// the exported selectors below, which expose the same data in a much more
// convenient form.
const rawSpecs = createSelector(getSpecState, specs => specs.specs.specs);
const rawSpecConfigs = createSelector(getSpecState, specs => specs.specs.specConfigs);
const rawSpecMappings = createSelector(getSpecState, specs =>
  specs.specs.specObjectMappingRecords.map(s => s.mapping!),
);

const rawSpecObjectConfigs = createSelector(getSpecState, specState =>
  keyBy(
    specState.specs.specObjectConfigRecords.map(s => s.config!),
    config => config.objectSlug,
  ),
);
const rawSpecObjectMappingRecords = createSelector(
  getSpecState,
  specState => specState.specs.specObjectMappingRecords,
);
const rawSpecObjectMappingsBySlug = createSelector(
  rawSpecObjectMappingRecords,
  (records: SpecObjectMappingRecord[]): BySlug<SpecObjectMappingInfo> =>
    // This keyBy() ensures that there's only ever one mapping per spec object.
    // When/if the time comes to implement 1:N mapping support, just swap out
    // this keyBy() for groupBy() and update the downstream selectors.
    keyBy(records.map(toSpecObjectMappingInfo).filter(isNonNull), record => record.objectSlug),
);
const rawSpecsAndObjects = createSelector(
  rawSpecs,
  rawSpecConfigs,
  rawSpecMappings,
  getOrgIndustry,
  getFlags,
  (specs, specConfigs, mappings, orgIndustry, flags): { spec: SpecInfo; objects: SpecObject[] }[] =>
    specs.map(spec => ({
      spec: toSpecInfo(
        spec,
        specConfigs.find(sc => sc.specSlug === spec.slug),
        mappings,
        orgIndustry,
        flags,
      ),
      objects: spec.objects,
    })),
);

export const allSpecsBySlug = createSelector(
  rawSpecsAndObjects,
  (specsAndObjects: { spec: SpecInfo; objects: SpecObject[] }[]): BySlug<SpecInfo> =>
    keyBy(
      specsAndObjects.map(({ spec }) => spec),
      spec => spec.slug,
    ),
);

export const allSpecs = createSelector(allSpecsBySlug, (specs: BySlug<SpecInfo>): SpecInfo[] =>
  Object.values(specs),
);

export const allSpecObjectsBySlug = createSelector(
  rawSpecsAndObjects,
  rawSpecObjectConfigs,
  rawSpecObjectMappingsBySlug,
  getFlags,
  (
    specsAndObjects: { spec: SpecInfo; objects: SpecObject[] }[],
    configs: BySlug<SpecObjectConfig>,
    mappings: BySlug<SpecObjectMappingInfo>,
    flags: FeatureFlags,
  ): BySlug<SpecObjectInfo> =>
    keyBy(
      specsAndObjects.flatMap(({ spec, objects }) =>
        objects.map(object =>
          toSpecObjectInfo(spec, object, configs[object.slug], mappings[object.slug], flags),
        ),
      ),
      object => object.slug,
    ),
);

export const allSpecObjectPropertiesBySlug = createSelector(
  allSpecObjectsBySlug,
  (objects: BySlug<SpecObjectInfo>): BySlug<SpecObjectProperty> =>
    keyBy(
      Object.values(objects).flatMap(object => object.properties),
      property => property.slug,
    ),
);

export const allSpecObjects = createSelector(
  allSpecObjectsBySlug,
  (objects: BySlug<SpecObjectInfo>): SpecObjectInfo[] => Object.values(objects),
);

export const allMappedSpecObjectsBySlug = createSelector(
  allSpecObjectsBySlug,
  (objects: BySlug<SpecObjectInfo>) =>
    pickBy(objects, object => object.mapping) as BySlug<
      WithRequiredProperty<SpecObjectInfo, 'mapping'>
    >,
);

export const allMappedSpecObjectPropertiesBySlug = createSelector(
  allMappedSpecObjectsBySlug,
  (objects: BySlug<WithRequiredProperty<SpecObjectInfo, 'mapping'>>) =>
    keyBy(
      Object.values(objects).flatMap(object => object.mapping.properties),
      (property: SpecPropertyMapping) => property.propertySlug,
    ),
);

export const allMappedSpecObjects = createSelector(
  allMappedSpecObjectsBySlug,
  (objects: BySlug<WithRequiredProperty<SpecObjectInfo, 'mapping'>>) => Object.values(objects),
);

export const allSpecApiEventsBySlug = createSelector(
  allSpecObjectsBySlug,
  (objects: BySlug<SpecObjectInfo>): BySlug<SpecObjectInfo> =>
    pickBy(objects, object => object.mappableTypes.includes(TargetType.ApiEvent)),
);

export const allSpecApiEvents = createSelector(
  allSpecApiEventsBySlug,
  (objects: BySlug<SpecObjectInfo>): SpecObjectInfo[] => Object.values(objects),
);

export const allSpecDefinedEventsBySlug = createSelector(
  allSpecObjectsBySlug,
  (objects: BySlug<SpecObjectInfo>): BySlug<SpecObjectInfo> =>
    pickBy(objects, object => object.mappableTypes.includes(TargetType.DefinedEvent)),
);

export const allSpecDefinedEvents = createSelector(
  allSpecDefinedEventsBySlug,
  (objects: BySlug<SpecObjectInfo>): SpecObjectInfo[] => Object.values(objects),
);

export const allSpecEventsBySlug = createSelector(
  allSpecObjectsBySlug,
  (objects: BySlug<SpecObjectInfo>): BySlug<SpecObjectInfo> =>
    pickBy(objects, object =>
      object.mappableTypes.some(
        targetType => targetType === TargetType.ApiEvent || targetType === TargetType.DefinedEvent,
      ),
    ),
);

export const allSpecEvents = createSelector(
  allSpecEventsBySlug,
  (objects: BySlug<SpecObjectInfo>): SpecObjectInfo[] => Object.values(objects),
);

export const allSpecElementsBySlug = createSelector(
  allSpecObjectsBySlug,
  (objects: BySlug<SpecObjectInfo>): BySlug<SpecObjectInfo> =>
    pickBy(objects, object => object.mappableTypes.includes(TargetType.Element)),
);

export const allSpecElements = createSelector(
  allSpecElementsBySlug,
  (objects: BySlug<SpecObjectInfo>): SpecObjectInfo[] => Object.values(objects),
);

export const allSpecPagesBySlug = createSelector(
  allSpecObjectsBySlug,
  (objects: BySlug<SpecObjectInfo>): BySlug<SpecObjectInfo> =>
    pickBy(objects, object => object.mappableTypes.includes(TargetType.Page)),
);

export const allSpecPages = createSelector(
  allSpecPagesBySlug,
  (objects: BySlug<SpecObjectInfo>): SpecObjectInfo[] => Object.values(objects),
);

export const getTopUnmappedSpecObjects = createSelector(
  getFeatureFlags,
  allSpecObjectsBySlug,
  (featureFlags, objectsBySlug): SpecObjectInfo[] => {
    if (!featureFlags.AppUnmappedSpecSuggestions) {
      return [];
    }
    const unmappedBySlug = pickBy(objectsBySlug, object => object.active && !object.mapping);
    return topSpecObjectSlugs.map(slug => unmappedBySlug[slug]).filter(isNonNull);
  },
);
